import * as dateMath from 'date-arithmetic';
import * as React from 'react';
import { Navigate } from 'react-router-dom';
import { ActivityType, SurveyActivityListing, assertNever, rng } from '../../amorg-common';
import { createActivity, getSurveyActivities } from '../../api/activityClientApi';
import { Button } from '../../designSystem/button/Button';
import { Flex } from '../../designSystem/compositeComponents/FlexHelpers';
import { LoadingPageOverlay } from '../../designSystem/LoadingPageOverlay';
import { PageHeader } from '../../designSystem/PageHeader';
import { PageSection } from '../../designSystem/PageSection';
import { PageSectionTable, PageSectionRow, PageSectionCell } from '../../designSystem/PageSectionTable';
import { SimpleStatCard, StatCardRow } from '../../designSystem/StatCard';
import { P } from '../../designSystem/typography/Body';
import { txt } from '../../txt';
import { clientAccessHelper } from '../../utils/clientAccessHelper';
import { toDateString } from '../../utils/date';
import { ProtectedPageContext } from '../_contexts/ProtectedPageContext';
import { SingleColumnLayout } from '../_layouts/SingleColumnLayout';
import { MemberPageWrapper } from '../_pageWrappers/MemberPageWrapper';
import { IconWithText } from '../../designSystem/compositeComponents/compositeIcons/IconWithText';

export default function WorkplaceAssessmentListingPage(): JSX.Element 
{

	return (
		<MemberPageWrapper selectedMenuItemKey="workplaceassessment" breadCrumbs={[{ text: "APV'er", url: '' }]}>
            <WorkplaceAssessmentListingPageContent />
		</MemberPageWrapper>
	);
}

function WorkplaceAssessmentListingPageContent(): JSX.Element
{
    const activeUser = React.useContext(ProtectedPageContext).user;
    const hasAccess = clientAccessHelper.hasAllPermissions(activeUser, ['ViewActivity']);

    if (!hasAccess)
        return <P>Hov! Du har ikke rettigheder til at tilgå denne side.</P>;

    return <WorkplaceAssessmentListingView />;
}


type State = {
    loading:boolean,
    activities:SurveyActivityListing[],
    redirectId:string
}
function WorkplaceAssessmentListingView(): JSX.Element
{
    const [state, setState] = React.useState<State>({
        loading:true,
        activities:[],
        redirectId:''
    });

    React.useEffect(()=>{
        const getData = async () => {
            const activities = await getSurveyActivities('WorkplaceAssessment');
            activities.sort((a,b)=> b.start.getTime() - a.start.getTime());
            // Get activities convert start and end from strings (JSON transport) to dates
            setState({activities, loading:false, redirectId:''});
        }
        getData();
    },[])

    function handleClickNavigate(activityId:string)
    {
        setState({...state, redirectId:activityId});
    }

    async function handleClickNew()
    {
        const now = new Date();

        const activityId = rng.getRandomId();
        const startDate = new Date(now.getFullYear(), now.getMonth()+1, 1, 8, 0);
        const endDate = dateMath.add(startDate, 1, 'month');
        const type:ActivityType = 'WorkplaceAssessment';

        setState({...state, loading:true});

        const result = await createActivity(
            activityId,
            'WorkplaceAssessment',
            txt.getDynamic(`NewActivityType_${type}`),
            '',
            startDate,
            endDate,
            ''
        );

        if(result.isSuccess){
            setState({...state, redirectId:activityId});
        }
    }

    const current = state.activities?.filter(a => a.end >= new Date()) || [];
    const past = state.activities?.filter(a => a.end < new Date()) || [];

    return (
        <>
            <PageHeader
                headerText="APV'er"
                headerIcon="assignment"
                buttons={
                    <Button variant="filled" text="Opret ny APV" icon="actions_plus" onClick={handleClickNew} />
                }
            />
            <SingleColumnLayout>
            <>
                {state.redirectId !== '' &&
                    <Navigate to={`/workplaceassessments/${state.redirectId}`} />
                }

                <LoadingPageOverlay loading={state.loading} />

                <StatCardRow>
                    <SimpleStatCard
                        header="Igangværende"
                        stat={current.filter(activity => activity.survey !== null).length.toString()}
                        subText="spørgeskemaer åbne"
                    />
                    <SimpleStatCard
                        header="Besvarelser"
                        stat={current.reduce((total, activity) => total + (activity.survey?.answerCount ?? 0), 0).toString()}
                        subText="medarbejdere har deltaget"
                    />
                    <SimpleStatCard
                        header="Uden handleplan"
                        stat={current.filter(activity => activity.actionPlanCount === 0).length.toString()}
                        subText={current.filter(activity => activity.actionPlanCount === 0).length > 0 ? "kræver opfølgning" : "Ingen handling nødvendig"}
                    />
                </StatCardRow>

                <PageSection
                    header={{
                        title: "Aktuelle APV'er",
                        icon: "assignment",
                        subTitle: { 
                            text: current.length > 0 ? `${current.length} i gang` : `Ingen aktuelle APV'er`
                        },
                    }}
                    headerStyleVariant='minimal'
                    contentStyleVariant='minimal'
                    contentPadding="16px 0"
                >
                <PageSectionTable
                    headerColumns={[
                        {text:'Periode', width:'360px'},
                        {text:'APV'},
                        {text:'Status', width:'600px'}
                    ]}
                    noContentText="Ingen aktuelle APV'er"
                    showAllLimit={5}>
                    {current.map(activity => (
                        <PageSectionRow key={activity.id} onClick={() => handleClickNavigate(activity.id)}>
                            <PageSectionCell><PeriodContent activity={activity} /></PageSectionCell>
                            <PageSectionCell><div>{activity.title}</div></PageSectionCell>
                            <PageSectionCell><CurrentSurveyStatus activity={activity} /></PageSectionCell>
                        </PageSectionRow>
                    ))}
                    </PageSectionTable>
                </PageSection>



                

                {past.length > 0 &&

                <PageSection

                    header={{
                        title: `Tidligere APV'er`,
                        icon: "assignment_turned_in",
                        subTitle: { 
                            text: ""
                        },
                    }}
                    headerStyleVariant='minimal'
                    contentStyleVariant='minimal'
                    contentPadding="16px 0"
                    >
                    <PageSectionTable
                        headerColumns={[
                            {text:'Periode', width:'360px'},
                            {text:'APV'},
                            {text:'Status', width:'600px'}
                        ]}
                        noContentText="Ingen tidligere APV'er"
                        showAllLimit={5}>
                        {past.map(activity => (
                            <PageSectionRow key={activity.id} onClick={() => handleClickNavigate(activity.id)}>
                                <PageSectionCell><PeriodContent activity={activity} /></PageSectionCell>
                                <PageSectionCell><div>{activity.title}</div></PageSectionCell>
                                <PageSectionCell><PastSurveyStatus activity={activity} /></PageSectionCell>
                            </PageSectionRow>
                        ))}
                    </PageSectionTable>
                </PageSection>
                }
            </>
        </SingleColumnLayout>
        </>
    );
}

function PeriodContent({activity}:{activity:SurveyActivityListing}):JSX.Element
{
    return <span>{toDateString(activity.start) + ' - ' + toDateString(activity.end)}</span>;
}

function CurrentSurveyStatus({activity}:{activity:SurveyActivityListing}):JSX.Element
{    
    const status = surveyStatus(activity);
    
    if(status === 'no-survey')
        return  <IconWithText icon="warning" variant="passive">Intet spørgeskema</IconWithText>
    
    if(status === 'waiting-to-open')
    {
        return <Flex flexDirection="row" alignItems="center" gap={32} rowGap={4} flexWrap="wrap">
                    <IconWithText icon="fact_check" variant="active">Planlagt</IconWithText>
                    <IconWithText icon="check_box" variant='default' tooltipContent="Antal spørgsmål i spørgeskemaet">{`${activity.survey?.questionCount}`}</IconWithText>
                    <IconWithText icon="interface_user_team" variant='default' tooltipContent="Antal besvarelser">{`${activity.survey?.answerCount}`}</IconWithText>
                </Flex>;
    }
            
    else if(status === 'closed')
    {
        return  <Flex flexDirection="row" alignItems="center" gap={32} rowGap={4} flexWrap="wrap">
                    <IconWithText icon="status_ok" variant="active">Afsluttet</IconWithText>
                    <IconWithText icon="interface_user_team" variant='default' tooltipContent="Antal besvarelser">{`${activity.survey?.answerCount}`}</IconWithText>
                </Flex>;
    }
    else if(status === 'open')
    {
        return  <Flex flexDirection="row" alignItems="center" gap={32} rowGap={4} flexWrap="wrap">
                    <IconWithText icon="interface_glyph_hourglass" variant="active">Spørgeskema åbent</IconWithText>
                    <IconWithText icon="interface_user_team" variant='default' tooltipContent="Antal besvarelser">{`${activity.survey?.answerCount}`}</IconWithText>
                </Flex>;
    }
    else
        assertNever(status);
}

function PastSurveyStatus({activity}:{activity:SurveyActivityListing}):JSX.Element
{
    const elements:JSX.Element[] = [];
    elements.push(
        <IconWithText icon="status_ok" variant="success">Afsluttet</IconWithText>
    );
    elements.push(activity.actionPlanCount === 0
        ? <IconWithText icon="warning" variant="warning">Ingen handleplaner oprettet</IconWithText>
        : <IconWithText icon="status_ok" variant="active">{`${activity.actionPlanCount} handleplaner oprettet`}</IconWithText>
    );

    return <Flex flexDirection="row" alignItems="center" gap={32} rowGap={4} flexWrap="wrap">
        {elements.map((element, index) => <React.Fragment key={index}>{element}</React.Fragment>)}
    </Flex>;
}

const surveyStatus = (activity:SurveyActivityListing): 'no-survey' | 'waiting-to-open' | 'open' | 'closed' => {
    if(activity.survey === null)
        return 'no-survey';
    const now = new Date();
    if(activity.survey.start > now)
        return 'waiting-to-open';
    if(activity.survey.end < now)
        return 'closed';
    return 'open';
}